import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  createSavedFolder,
  deleteSavedItem,
  fetchSavedLibrary,
  renameSavedFolder,
  placeSavedItem,
  reorderSavedFolders,
  saveSavedItem,
} from '../services/savedItemsApi';
import {
  SaveItemInput,
  SavedFolder,
  SavedItem,
  SavedItemType,
  LOCAL_DEFAULT_FOLDER_ID,
  placeholderDefaultFolder,
  savedItemKey,
} from '../types/savedItems';

interface SavedItemsContextValue {
  ready: boolean;
  folders: SavedFolder[];
  refresh: () => Promise<void>;
  findItem: (type: SavedItemType, id: number | string) => SavedItem | undefined;
  saveItem: (input: SaveItemInput) => Promise<SavedItem>;
  createFolder: (name: string) => Promise<SavedFolder>;
  renameFolder: (id: string, name: string) => Promise<SavedFolder>;
  reorderFolders: (folderIds: string[]) => Promise<void>;
  placeItem: (itemId: string, folderId: string, index: number) => Promise<void>;
  unsaveItem: (id: string) => Promise<void>;
}

const SavedItemsContext = createContext<SavedItemsContextValue | null>(null);

function savedItemOrderKey(folders: SavedFolder[]): string {
  return folders.map((folder) => folder.items.map((item) => item.id).join(',')).join('|');
}

function applyItemPlace(
  folders: SavedFolder[],
  itemId: string,
  folderId: string,
  index: number
): SavedFolder[] {
  const source = folders.find((folder) => folder.items.some((item) => item.id === itemId));
  const item = source?.items.find((entry) => entry.id === itemId);
  const target = folders.find((folder) => folder.id === folderId);
  if (!source || !item || !target) return folders;

  return folders.map((folder) => {
    if (folder.id !== source.id && folder.id !== target.id) return folder;
    const remaining = folder.items.filter((entry) => entry.id !== itemId);
    if (folder.id !== target.id) {
      return {
        ...folder,
        items: remaining.map((entry, position) => ({ ...entry, sortOrder: position })),
      };
    }
    const at = Math.max(0, Math.min(index, remaining.length));
    const items = [...remaining];
    items.splice(at, 0, { ...item, folderId: target.id });
    return {
      ...folder,
      items: items.map((entry, position) => ({ ...entry, sortOrder: position })),
    };
  });
}

export function SavedItemsProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const [folders, setFolders] = useState<SavedFolder[]>([]);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSignedIn) {
      setFolders([]);
      setReady(true);
      return;
    }
    try {
      const library = await fetchSavedLibrary(getToken);
      const next = Array.isArray(library.folders) ? library.folders : [];
      setFolders(next.length > 0 ? next : [placeholderDefaultFolder()]);
    } catch (error) {
      console.error('Failed to load saved items:', error);
      setFolders([placeholderDefaultFolder()]);
    } finally {
      setReady(true);
    }
  }, [getToken, isSignedIn]);

  useEffect(() => {
    if (!isLoaded) return;
    void refresh();
  }, [isLoaded, refresh]);

  const findItem = useCallback(
    (type: SavedItemType, id: number | string) => {
      const key = savedItemKey(type, id);
      for (const folder of folders) {
        const match = folder.items.find((item) => item.itemKey === key);
        if (match) return match;
      }
      return undefined;
    },
    [folders]
  );

  const saveItem = useCallback(
    async (input: SaveItemInput) => {
      const folderId =
        !input.folderId || input.folderId === LOCAL_DEFAULT_FOLDER_ID
          ? undefined
          : input.folderId;
      const item = await saveSavedItem(getToken, { ...input, folderId });
      await refresh();
      return item;
    },
    [getToken, refresh]
  );

  const createFolder = useCallback(
    async (name: string) => {
      const folder = await createSavedFolder(getToken, name);
      await refresh();
      return folder;
    },
    [getToken, refresh]
  );

  const renameFolder = useCallback(
    async (id: string, name: string) => {
      const folder = await renameSavedFolder(getToken, id, name);
      await refresh();
      return folder;
    },
    [getToken, refresh]
  );

  const reorderFolders = useCallback(
    async (folderIds: string[]) => {
      const previous = folders;
      setFolders((current) => {
        const byId = new Map(current.map((folder) => [folder.id, folder]));
        return folderIds.flatMap((id, index) => {
          const folder = byId.get(id);
          return folder ? [{ ...folder, sortOrder: index }] : [];
        });
      });
      try {
        const library = await reorderSavedFolders(getToken, folderIds);
        setFolders(Array.isArray(library.folders) ? library.folders : previous);
      } catch (error) {
        setFolders(previous);
        throw error;
      }
    },
    [folders, getToken]
  );

  const placeItem = useCallback(
    async (itemId: string, folderId: string, index: number) => {
      const previous = folders;
      const next = applyItemPlace(previous, itemId, folderId, index);
      if (savedItemOrderKey(next) === savedItemOrderKey(previous)) return;
      setFolders(next);
      try {
        const library = await placeSavedItem(getToken, itemId, folderId, index);
        setFolders(Array.isArray(library.folders) && library.folders.length > 0 ? library.folders : previous);
      } catch (error) {
        setFolders(previous);
        throw error;
      }
    },
    [folders, getToken]
  );

  const unsaveItem = useCallback(
    async (id: string) => {
      await deleteSavedItem(getToken, id);
      await refresh();
    },
    [getToken, refresh]
  );

  const value = useMemo(
    () => ({
      ready,
      folders,
      refresh,
      findItem,
      saveItem,
      createFolder,
      renameFolder,
      reorderFolders,
      placeItem,
      unsaveItem,
    }),
    [createFolder, findItem, folders, placeItem, ready, refresh, renameFolder, reorderFolders, saveItem, unsaveItem]
  );

  return createElement(SavedItemsContext.Provider, { value }, children);
}

export function useSavedItems() {
  const context = useContext(SavedItemsContext);
  if (!context) {
    throw new Error('useSavedItems must be used within SavedItemsProvider');
  }
  return context;
}
